import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import { luhnCheck } from "@@/utilities/luhn-check";
import { tryParseInt } from "@@/utilities/try-parse-int";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    _PayoutTargetBase} from "./_payout-target-base";
import {
    payoutTargetBaseZodSchema,
} from "./_payout-target-base";
import type {
    PayoutTargetId} from "./payout-target-id";
import {
    payoutTargetIdFactory,
    payoutTargetIdZodSchema,
} from "./payout-target-id";

type PayoutTargetBankgiroType = "bankgiro";
const payoutTargetBankgiroTypeZodSchema = z.literal("bankgiro");

type PayoutTargetBankgiro = _PayoutTargetBase & {
    _id: PayoutTargetId;
    type: PayoutTargetBankgiroType;
    bankgiro: string;
    customMessage?: string | null;
};

const bankGiroZodSchema = z
    .string()
    .refine(
        value => {
            const digits = value.replace(/[^\d]+/g, "");
            const number = tryParseInt(digits, undefined);
            if (typeof number === "undefined") return false;
            return digits.length >= 7 && digits.length <= 8;
        },
        { message: "bankgironummer måste innehålla 7 till 8 siffror" },
    )
    .refine(
        value => {
            const digits = value.replace(/[^\d]+/g, "");
            // console.log("LUNCHCHECK", value, digits);
            return luhnCheck(digits, true);
        },
        {
            message: "ogiltigt bankgironummer",
        },
    )
    .transform(value => {
        const digits = value.replace(/[^\d]+/g, "");
        const firstSliceLength = digits.length === 8 ? 4 : 3;
        return `${digits.slice(0, firstSliceLength)}-${digits.slice(
            firstSliceLength,
        )}`;
    });
type Params = Except<
    PayoutTargetBankgiro,
    "_id" | "type" | "_type" | "_createdAt"
>;
const bankGiroFactory = (params: Params): PayoutTargetBankgiro => {
    return {
        ...params,
        _id: payoutTargetIdFactory(),
        _type: "PAYOUT_TARGET",
        _createdAt: nowIsoAndUnix(),
        type: "bankgiro",
    };
};

const payoutTargetBankgiroZodSchema: z.ZodSchema<PayoutTargetBankgiro> =
    z.intersection(
        payoutTargetBaseZodSchema,
        z.object({
            _id: payoutTargetIdZodSchema,
            type: payoutTargetBankgiroTypeZodSchema,
            bankgiro: bankGiroZodSchema,
            customMessage: z.string().nullable(),
        }),
    );

export {
    bankGiroFactory,
    bankGiroZodSchema,
    payoutTargetBankgiroTypeZodSchema,
    payoutTargetBankgiroZodSchema,
};
export type { PayoutTargetBankgiro, PayoutTargetBankgiroType };
